(function () {
  'use strict';

  class HpgTabs extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.tabs = Array.from(this.querySelectorAll('[data-hpg-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-hpg-tab-panel]'));
      this.tablist = this.querySelector('[role="tablist"]');
      this.dragState = null;
      this.didDragTablist = false;

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.setActive(tab.dataset.hpgTab));
      });

      this.setupTablistDrag();
      this.setActive(this.tabs[0]?.dataset.hpgTab || '0');
    }

    setupTablistDrag() {
      if (!this.tablist) return;

      const canScroll = () => this.tablist.scrollWidth > this.tablist.clientWidth;
      const startDrag = (clientX, pointerId = null) => {
        if (!canScroll()) return false;

        this.dragState = {
          pointerId,
          startX: clientX,
          startScrollLeft: this.tablist.scrollLeft,
        };
        this.didDragTablist = false;
        this.tablist.classList.add('is-dragging');
        return true;
      };

      const updateDrag = (clientX, pointerId = null) => {
        if (!this.dragState || this.dragState.pointerId !== pointerId) return;

        const deltaX = clientX - this.dragState.startX;
        if (Math.abs(deltaX) > 4) {
          this.didDragTablist = true;

          if (Number.isInteger(pointerId) && !this.tablist.hasPointerCapture(pointerId)) {
            this.tablist.setPointerCapture(pointerId);
          }
        }

        this.tablist.scrollLeft = this.dragState.startScrollLeft - deltaX;
      };

      const stopDrag = (pointerId = null) => {
        if (!this.dragState || this.dragState.pointerId !== pointerId) return;

        this.tablist.classList.remove('is-dragging');
        this.dragState = null;
      };

      if (window.PointerEvent) {
        this.tablist.addEventListener('pointerdown', (event) => {
          if (event.button !== 0 || !startDrag(event.clientX, event.pointerId)) return;
        });

        this.tablist.addEventListener('pointermove', (event) => updateDrag(event.clientX, event.pointerId));
        this.tablist.addEventListener('pointerup', (event) => this.endTablistPointerDrag(event, stopDrag));
        this.tablist.addEventListener('pointercancel', (event) => this.endTablistPointerDrag(event, stopDrag));
      } else {
        this.tablist.addEventListener('mousedown', (event) => {
          if (event.button !== 0 || !startDrag(event.clientX, 'mouse')) return;

          const onMouseMove = (moveEvent) => {
            updateDrag(moveEvent.clientX, 'mouse');
          };
          const onMouseUp = () => {
            stopDrag('mouse');
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
          };

          event.preventDefault();
          window.addEventListener('mousemove', onMouseMove);
          window.addEventListener('mouseup', onMouseUp);
        });
      }

      this.tablist.addEventListener('click', (event) => {
        if (!this.didDragTablist) return;

        event.preventDefault();
        event.stopPropagation();
        this.didDragTablist = false;
      }, true);
    }

    endTablistPointerDrag(event, stopDrag) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

      if (this.tablist.hasPointerCapture(event.pointerId)) {
        this.tablist.releasePointerCapture(event.pointerId);
      }
      stopDrag(event.pointerId);
    }

    setActive(index) {
      let activeTab = null;

      this.tabs.forEach((tab) => {
        const active = tab.dataset.hpgTab === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active) activeTab = tab;
      });

      this.panels.forEach((panel) => {
        const active = panel.dataset.hpgTabPanel === index;
        panel.classList.toggle('is-active', active);
        panel.toggleAttribute('hidden', !active);
      });

      if (activeTab && this.tablist && this.tablist.scrollWidth > this.tablist.clientWidth) {
        const tablistRect = this.tablist.getBoundingClientRect();
        const tabRect = activeTab.getBoundingClientRect();
        const target = this.tablist.scrollLeft + tabRect.left - tablistRect.left;
        this.tablist.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
      }
    }
  }

  if (!customElements.get('hpg-tabs')) {
    customElements.define('hpg-tabs', HpgTabs);
  }
})();
